import { useMemo } from 'react';

import { IFeatureCateringPageQuery, useFeatureCateringPageQuery } from 'generated/sanity-graphql';
import { useAuthContext } from 'state/auth';
import { useFeaturesContext } from 'state/features';
import { isNative } from 'utils/environment';

export const processFeatureCateringPage = ({
  data,
  isAuthenticated,
  isApp,
}: {
  data?: IFeatureCateringPageQuery;
  isAuthenticated: boolean;
  isApp: boolean;
}) => {
  const featureCateringPage = data?.FeatureCateringPage;
  if (!featureCateringPage) {
    return null;
  }

  // Keep it verbose to make things clear and to keep type safety
  if (isApp) {
    if (isAuthenticated) {
      return featureCateringPage?.appAuthenticated;
    }
    return featureCateringPage?.appUnauthenticated;
  }
  if (isAuthenticated) {
    return featureCateringPage?.webAuthenticated;
  }
  return featureCateringPage?.webUnauthenticated;
};

export const useFeatureCateringPage = () => {
  const { featureCateringPageId = '', featureIdsLoading } = useFeaturesContext();
  const { isAuthenticated } = useAuthContext();

  const isUserAuthenticated = isAuthenticated();

  const { data, loading } = useFeatureCateringPageQuery({
    variables: { featureCateringPageId },
    skip: !featureCateringPageId,
  });

  const featureCateringPage = useMemo(
    () =>
      processFeatureCateringPage({ data, isAuthenticated: isUserAuthenticated, isApp: isNative }),
    [data, isUserAuthenticated]
  );
  const featureCateringPageLoading = featureIdsLoading || loading;

  return {
    featureCateringPageLoading,
    featureCateringPage,
  };
};
